import { createApi } from '@reduxjs/toolkit/query/react';

import { ResponseProps } from 'services/services.types';

import { GenericEligibilityCheckResponse } from 'hooks/usePaywall/usePaywall.types';
import { setProgramsCatalog } from 'store/programs/programsCatalogSlice';
import { setPrograms } from 'store/programs/programsSlice';
import { baseQueryWithReauth } from 'utils/services';

import { ProgramCategory, UserProgram } from 'models/programs.types';

import {
  AddProgramResProps,
  CancelAutoRenewReqProps,
  CancelAutoRenewResProps,
  GetMyProgramsReqProps,
  GetMyProgramsResProps,
  GetProgramsReqProps,
  GetProgramsResProps,
  UpdateProgramReqProps
} from './programs.types';

export const programsApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    abortCancellation: build.mutation<{ userProgram: UserProgram }, { programId: string }>({
      invalidatesTags: ['Programs'],
      query: ({ programId }) => ({
        method: 'PATCH',
        url: `/my-programs/${programId}/abort-delayed-cancellation`
      })
    }),
    addProgram: build.mutation<AddProgramResProps, UpdateProgramReqProps>({
      invalidatesTags: ['Programs'],
      query: (body) => ({ body, method: 'POST', url: '/add-program' })
    }),
    cancelAutoRenew: build.mutation<CancelAutoRenewResProps, CancelAutoRenewReqProps>({
      invalidatesTags: ['Programs'],
      query: (body) => ({ body, method: 'POST', url: '/my-programs/cancel-auto-renew' })
    }),
    // TEMPORARY (LMD-614): direct cancel for Women's Health programs (no Typeform downsell flow yet).
    cancelProgram: build.mutation<unknown, { programId: string }>({
      invalidatesTags: ['Programs'],
      query: ({ programId }) => ({
        method: 'POST',
        url: `/my-programs/${programId}/cancel`
      })
    }),
    checkProgramEligibility: build.mutation<
      ResponseProps<GenericEligibilityCheckResponse['data']>,
      { programCategory: ProgramCategory } | { programCode: string }
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: `/programs/onboarding/eligibility`
      })
    }),
    getMyPrograms: build.query<UserProgram[], GetMyProgramsReqProps>({
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(setPrograms(data));
          }
        } catch {
          // handled by RTK Query cache
        }
      },
      providesTags: ['Programs'],
      query: (params) => {
        const p = params || {};
        const { accessToken, ...rest } = p;
        return {
          params: {
            includeInactive: true,
            ...rest,
            // Always request both so a caller-supplied expand cannot drop one of them.
            expand: ['details', 'subscription']
          },
          ...(accessToken && {
            headers: {
              Authorization: accessToken
            }
          }),
          url: '/my-programs'
        };
      },
      transformResponse: (response: GetMyProgramsResProps) => response.data
    }),
    getPrograms: build.query<GetProgramsResProps, GetProgramsReqProps>({
      keepUnusedDataFor: 3600, // 1 hour,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.data) {
            dispatch(setProgramsCatalog(data.data));
          }
        } catch {
          // handled by RTK Query cache
        }
      },
      query: (params) => ({
        ...(params && { params }),
        url: '/programs'
      })
    }),
    updatePreferredMedication: build.mutation<
      unknown,
      { preferredMedication: string; programId: string }
    >({
      invalidatesTags: ['Programs'],
      query: ({ preferredMedication, programId }) => ({
        body: { preferredMedication },
        method: 'PATCH',
        url: `/my-programs/${programId}/preferred-medication`
      })
    }),
    updateProgram: build.mutation<unknown, UpdateProgramReqProps>({
      invalidatesTags: ['Programs'],
      query: ({ programId, ...body }) => ({
        body: { ...body, programId },
        method: 'PATCH',
        url: `/update-program/${programId}`
      })
    })
  }),
  reducerPath: 'programsApi',
  tagTypes: ['Programs']
});

export const {
  useAbortCancellationMutation,
  useAddProgramMutation,
  useCancelAutoRenewMutation,
  useCancelProgramMutation,
  useCheckProgramEligibilityMutation,
  useGetMyProgramsQuery,
  useGetProgramsQuery,
  useLazyGetMyProgramsQuery,
  useLazyGetProgramsQuery,
  useUpdatePreferredMedicationMutation,
  useUpdateProgramMutation
} = programsApi;
